import { kebabCaseToCamelCase } from "serenova-js-utils/strings";
import { typeName } from "enzyme/build/Debug";
import { ConfidentialClientApplication } from "@azure/msal-node";

export const initialProperties = {
  rest: { endpointPrefix: '', password: '', token: '', username: '' },
  salesforce: {
    consumerKey: '', consumerSecret: '', password: '', securityToken: '', username: '', // v1 fields
    authVersion: 'v2-auth-code', refreshToken: '', instanceUrl: '', connectionState: '', loginUrl: '' // v2 fields
  },
  zendesk: { endpointPrefix: '', interactionFieldId: '', password: '', username: '', workItems: false },
  'ms-teams': { extensions: true },
  activedir: { azureTenantId: '', applicationId: '', secret: '', groupsFilter: '', locationsFilter: '' },
  ecg: { trunkId: '', webRtc: false, extensionPrefix: '' },
  socialconnector:[{label:'',value:''}],

};

export const getIntegrationProperties = (type = 'rest') => {
  return initialProperties[type] || {};
};

const isMaskedSecretPlaceholder = (value) => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return /^\*{4,}$/.test(trimmed);
};

export const prepareIntegrationProperties = (type = 'rest', authType = 'noAuth', properties,data) => {
  const defaultProperties = getIntegrationProperties(type);
  const updatedProperties = { ...defaultProperties, ...properties };
  if (type === 'zendesk') {
    delete updatedProperties.token;
  } else if (type === 'rest') {
    const resolvedAuthType = authType || updatedProperties.authType || 'noAuth';
    delete updatedProperties.workItems;
    delete updatedProperties.authType;
    if (resolvedAuthType === 'basic') {
      updatedProperties.token = '';
    } else if (resolvedAuthType === 'token') {
      updatedProperties.username = updatedProperties.password = '';
    } else {
      updatedProperties.username = updatedProperties.password = updatedProperties.token = '';
    }
    // If the UI placed masked placeholders for hidden secrets, remove them so the
    // backend preserves the real values instead of overwriting with the mask.
    if (isMaskedSecretPlaceholder(updatedProperties.token)) {
      delete updatedProperties.token;
    }
    if (isMaskedSecretPlaceholder(updatedProperties.password)) {
      delete updatedProperties.password;
    }
  } else if (type === 'salesforce') {
    const hasAuthVersion = properties && Object.prototype.hasOwnProperty.call(properties, 'authVersion');
    if (!hasAuthVersion) {
      const hasLegacyCredentials = Boolean(
        properties && (
          properties.username ||
          properties.password ||
          properties.securityToken ||
          properties.consumerKey ||
          properties.consumerSecret
        )
      );
      updatedProperties.authVersion = hasLegacyCredentials ? 'v1-password' : 'v2-auth-code';
    }

    // Remove legacy fields for v2
    if (updatedProperties.authVersion === 'v2-auth-code') {
      delete updatedProperties.username;
      delete updatedProperties.password;
      delete updatedProperties.securityToken;
      delete updatedProperties.consumerKey;
      delete updatedProperties.consumerSecret;
    } else {
      // Remove v2 fields for v1
      delete updatedProperties.refreshToken;
      delete updatedProperties.instanceUrl;
      delete updatedProperties.connectionState;
    }
    delete updatedProperties.endpointPrefix;
    delete updatedProperties.token;
    delete updatedProperties.workItems;
  } else if (type === 'twilio') {
    delete updatedProperties.sdkVersion;
    if (isMaskedSecretPlaceholder(updatedProperties.authToken)) {
      delete updatedProperties.authToken;
    }
  } else if (type === 'ms-teams') {
    delete updatedProperties.endpointPrefix;
    delete updatedProperties.token;
    delete updatedProperties.username;
    delete updatedProperties.password;
  } else if (type === 'msteams-presence') {
    delete updatedProperties.endpointPrefix;
    delete updatedProperties.token;
    delete updatedProperties.username;
    delete updatedProperties.password;
  } else if (type === 'activedir') {
    delete updatedProperties.endpointPrefix;
    delete updatedProperties.token;
    delete updatedProperties.username;
    delete updatedProperties.password;
  } else if (type === 'salesforce-service-cloud') {
    delete updatedProperties.endpointPrefix;
    delete updatedProperties.token;
    delete updatedProperties.callCentreName; // need to delete this once multi integration enable from backend
  } else if (type === 'ecg') {
    delete updatedProperties.endpointPrefix;
    delete updatedProperties.token;
    delete updatedProperties.username;
    delete updatedProperties.password;
   }
   else if (type === 'socialconnector') {
    return data.values.socialConnectorProperties.reduce((acc, { label, value }) => {
      acc[label] = value;
      return acc;
    }, {});
  }
  return updatedProperties;
}

export const prepareEmailProperties = (properties) => {
  if ((properties.incomingType === 'oauth-google' || properties.incomingType === 'oauth-ms')
    && properties.oauthCredential) {
    if (typeof properties.oauthCredential === 'string') {
      let oauthCredential = JSON.parse(properties.oauthCredential);
      properties.oauthCredential = oauthCredential;
    }
    delete properties.smtpPassword;
  } else if (properties.incomingType === 'imap') {
    delete properties.oauthCredential;
    delete properties.oauthCredentialId;
  }
  return properties;
}

export const prepareEmailListenerProperties = (properties, entityProperties) => {
  if (entityProperties.incomingType === 'imap') {
    delete properties.oauthCredential;
    delete properties.oauthCredentialId;
  } else {
    if (typeof properties.oauthCredential === 'string') {
      let oauthCredential = JSON.parse(properties.oauthCredential);
      properties.oauthCredential = oauthCredential;
    }
    delete properties.password;
  }
  return properties;
}

export const convertOauthStringToJson = (oauthString) => {
  let newOauthObject;
  try {
    newOauthObject = JSON.parse(oauthString);
    if (newOauthObject) {
      return newOauthObject;
    }
  } catch (e) {
    console.warn('Could not convert JSON object. ' + e);
  }

  let newOauthString = oauthString
    .replaceAll('{:', '')
    .replaceAll(' \"', ':')
    .replaceAll('\", :', ',')
    .replaceAll(', :', ',')
    .replaceAll(' ', ':')
    .replaceAll('\"}', '');
  const keyArray = newOauthString.split(',');
  let keyMap = new Map();
  keyArray.forEach((item) => {
    const keyValue = item.split(':');
    if (item.includes('https')) {
      keyMap.set(kebabCaseToCamelCase(keyValue[0]), keyValue[1] + ':' + keyValue[2]);
    } else if (keyValue[0].includes('expiresIn')) {
      keyMap.set(kebabCaseToCamelCase(keyValue[0]), parseInt(keyValue[1]));
    } else {
      keyMap.set(kebabCaseToCamelCase(keyValue[0]), keyValue[1]);
    }
  });
  const oauthObject = {};
  for (let [k, v] of keyMap) {
    oauthObject[k] = v;
  }

  const oauthObjectString = JSON.stringify(oauthObject);
  newOauthObject = JSON.parse(oauthObjectString);

  return newOauthObject;
};

export const convertOauthResponseToJson = (oauthString) => {
  let newJson = {};
  let keysObject = oauthString;
  if (typeof oauthString === 'string') {
    keysObject = JSON.parse(oauthString);
  }
  for (const key in keysObject) {
    newJson[kebabCaseToCamelCase(key)] = keysObject[key];
  }

  return newJson;
};

export function msLogin(baseUrl, msOauthClientId, msOauthClientSecret) {
  const msalConfig = {
    auth: {
      clientId: msOauthClientId,
      clientSecret: msOauthClientSecret,
      authority: 'https://login.microsoftonline.com/common/',
      authorityMetadata: JSON.stringify({
        authorization_endpoint: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
        token_endpoint: "https://login.microsoftonline.com/common/oauth2/v2.0/token",
        issuer: "https://login.microsoftonline.com/common/v2.0",
      })
    },
  };

  const msalInstance = new ConfidentialClientApplication(msalConfig);

  const authCodeUrlParameters = {
    clientId: msOauthClientId,
    responseType: 'code',
    redirectUri: baseUrl,
    responseMode: 'query',
    scopes: [
      'openid',
      'offline_access',
      'https://outlook.office.com/IMAP.AccessAsUser.All',
      'https://outlook.office.com/POP.AccessAsUser.All',
      'https://outlook.office.com/SMTP.send'
    ],
    prompt: 'select_account',
  };

  // get url to sign user in and consent to the scopes needed for application
  return msalInstance.getAuthCodeUrl(authCodeUrlParameters);
}

export const getRelativeUrl = () => {
  const relativeUrl = window.location.href;
  let position = 0;
  for (let i = 0; i < 3; i++) {
    position = relativeUrl.indexOf('/', position + 1);
  }
  return relativeUrl.substring(0, position);
};

export const getXHRInstance = (body) => {
  const xhr = new XMLHttpRequest();
  xhr.open('POST', 'https://oauth2.googleapis.com/token', true);
  xhr.setRequestHeader('Content-Type', 'application/json');
  return xhr;
};
